import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Package } from "lucide-react";

import { get } from "@/lib/api-client";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { Card, CardBody } from "@/components/ui/Card";
import { Tabs } from "@/components/ui/Tabs";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import type { Order } from "@/types/order";

export function OrdersPage() {
  const [tab, setTab] = useState("all");
  const { data, isLoading } = useQuery({
    queryKey: ["orders-list"],
    queryFn: () => get<Order[]>("/orders/orders/list"),
  });

  const orders = data ?? [];
  const filtered =
    tab === "all"      ? orders
  : tab === "pending"  ? orders.filter((o) => o.status === "pending_payment" || o.status === "payment_verification_pending")
  : tab === "completed"? orders.filter((o) => o.status === "completed")
  : orders.filter((o) => o.status === "cancelled" || o.status === "rejected");

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">My Orders</h1>
        <p className="text-sm text-surface-500 mt-1">Track every order and its payment status.</p>
      </header>

      <Card>
        <div className="px-5 pt-3">
          <Tabs value={tab} onChange={setTab}
                tabs={[
                  { value: "all",       label: "All",       count: orders.length },
                  { value: "pending",   label: "Pending" },
                  { value: "completed", label: "Completed" },
                  { value: "closed",    label: "Closed" },
                ]} />
        </div>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState icon={Package} title="No orders in this view" />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Order ID</TH>
                  <TH>Placed</TH>
                  <TH>Items</TH>
                  <TH>Total</TH>
                  <TH>Payment</TH>
                  <TH>Status</TH>
                </TR>
              </THead>
              <tbody>
                {filtered.map((o) => (
                  <TR key={o.order_id}>
                    <TD className="font-mono text-xs">
                      <Link to={`/dashboard/orders/${o.order_id}`} className="text-brand-600 font-semibold hover:text-brand-700">
                        {o.order_id}
                      </Link>
                    </TD>
                    <TD className="text-xs text-surface-600">{formatDateTime(o.placed_at)}</TD>
                    <TD>{o.items.length}</TD>
                    <TD className="font-semibold">{formatCurrency(o.total)}</TD>
                    <TD><StatusPill status={o.payment_status} /></TD>
                    <TD><StatusPill status={o.status} /></TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
